/**
 * Career Explorer cards — four doors, wired to the latest Career DNA™ output.
 *
 * Presentation only: names, clusters and the "why" sentence come verbatim from
 * the sealed recommendation rows. Nothing is ranked or re-worded here. When
 * there is no Career DNA™ yet, the cards become four honest invitations
 * instead of four empty boxes.
 *
 * Each card is a single focusable link with a visible focus ring and an
 * accessible name that reads on its own ("Mechatronics Technician, best match,
 * in Engineering & Making").
 */
import { Link } from "react-router-dom";
import { ArrowRight, Compass, Fingerprint, GraduationCap, Sparkles } from "lucide-react";
import { useCareerDna } from "@/hooks/useCareerDna";
import { useAssessmentEntry } from "@/hooks/useAssessmentEntry";


const CARD_FOCUS =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background";

const TIER_LABEL: Record<string, string> = {
  best: "Best match",
  good: "Strong match",
  explore: "Worth exploring",
  hidden: "Hidden gem",
};

const OTHER_DOORS = [
  {
    icon: Compass,
    title: "Browse all careers",
    body: "5,000+ roles you can search by name, family or level.",
    to: "/careers",
  },
  {
    icon: GraduationCap,
    title: "Build a learning plan",
    body: "Foundations first, proof second. Tracks sized to your week.",
    to: "/growth-plan",
  },
  {
    icon: Sparkles,
    title: "Ask YoMA",
    body: "Not sure where to start? Say so in plain words.",
    to: "/coach",
  },
] as const;

export default function ExplorerCards() {
  const { data: dna } = useCareerDna();
  const entry = useAssessmentEntry();
  const matches = dna?.matches ?? [];

  // The first door is never a dead end: it points at the finished run, the
  // half-finished run, or the band picker — whichever the learner actually has.
  const fallback = [
    { icon: Fingerprint, title: entry.label, body: entry.body, to: entry.to },
    ...OTHER_DOORS,
  ];


  return (
    <section className="calm-card p-6 sm:p-8" aria-labelledby="explorer-cards-heading">
      <h3
        id="explorer-cards-heading"
        className="font-display text-sm font-semibold uppercase tracking-[0.14em] text-muted-foreground"
      >
        {matches.length > 0 ? "Careers that fit you" : "Start exploring"}
      </h3>

      <ul className="mt-4 grid gap-3 sm:grid-cols-2">
        {matches.length > 0
          ? matches.slice(0, 4).map((m) => {
              const tier = m.tier ? (TIER_LABEL[m.tier] ?? m.tier) : null;
              return (
                <li key={m.id || m.name}>
                  <Link
                    to={`/careers/${encodeURIComponent(m.id)}`}
                    aria-label={`${m.name}${tier ? `, ${tier.toLowerCase()}` : ""}${m.cluster ? `, in ${m.cluster}` : ""}`}
                    className={`calm-card-quiet flex h-full min-h-[128px] flex-col gap-2 p-4 transition-colors hover:border-primary/40 hover:bg-primary/5 ${CARD_FOCUS}`}
                  >
                    {tier && (
                      <span className="inline-flex w-fit items-center rounded-full bg-primary/12 px-2.5 py-0.5 text-[10px] font-medium uppercase tracking-[0.12em] text-primary">
                        {tier}
                      </span>
                    )}
                    <span className="font-display text-base font-semibold leading-snug text-foreground">
                      {m.name}
                    </span>
                    {m.explanation && (
                      <span className="line-clamp-3 text-sm leading-relaxed text-muted-foreground">
                        {m.explanation}
                      </span>
                    )}
                    <span className="mt-auto inline-flex items-center gap-1.5 text-xs font-medium text-primary">
                      See why it fits
                      <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                    </span>
                  </Link>
                </li>
              );
            })
          : fallback.map((c) => (
              <li key={c.title}>
                <Link
                  to={c.to}
                  className={`calm-card-quiet flex h-full min-h-[128px] flex-col gap-2 p-4 transition-colors hover:border-primary/40 hover:bg-primary/5 ${CARD_FOCUS}`}
                >
                  <c.icon className="h-5 w-5 text-primary" aria-hidden="true" />
                  <span className="font-display text-base font-semibold leading-snug text-foreground">
                    {c.title}
                  </span>
                  <span className="text-sm leading-relaxed text-muted-foreground">{c.body}</span>
                  <span className="mt-auto inline-flex items-center gap-1.5 text-xs font-medium text-primary">
                    Open
                    <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                  </span>
                </Link>
              </li>
            ))}
      </ul>
    </section>
  );
}
